import { colors } from '@/constants/colors'
import { Header } from '@/ui/core/header'
import { Text } from '@/ui/core/typography'
import { View } from 'react-native'
import Svg, { Path } from 'react-native-svg'

/**
 * The arc's geometry, in points.
 *
 * Two hundred and forty degrees rather than a circle or a semicircle: a closed ring has no start
 * and no end, so it says how full something is without saying where on a scale you are, and a
 * semicircle reads as a dial. The mouth at the bottom keeps the proportion reading and still has
 * two ends. Past about 280° the gap stops looking deliberate and starts looking like a ring that
 * failed to finish drawing, which is the one thing a score must not look like.
 */
const R = 74
const STROKE = 12
const SWEEP = 240

/**
 * The figure's own block, and where it sits.
 *
 * Not the centre of the box. The arc runs past three o'clock, so the box is half a radius deeper
 * below the ring's centre than above it, and a figure laid out with `justifyContent: 'center'`
 * stands that half-radius high — which is exactly what it looked like. The block is pinned to the
 * ring's own centre instead, which means its height has to be known rather than measured.
 */
const FIGURE = 54
const LABEL_GAP = 6
const LABEL = 14
const BLOCK = FIGURE + LABEL_GAP + LABEL

const rad = (deg: number) => (deg * Math.PI) / 180

const PAD = STROKE / 2 + 3
const WIDTH = 2 * (R + PAD)
const CX = WIDTH / 2
const CY = R + PAD
const START = -90 - SWEEP / 2
const END = -90 + SWEEP / 2
const HEIGHT = CY + R * Math.sin(rad(END)) + PAD
const LENGTH = 2 * Math.PI * R * (SWEEP / 360)

const at = (angle: number) =>
	`${(CX + R * Math.cos(rad(angle))).toFixed(2)} ${(
		CY +
		R * Math.sin(rad(angle))
	).toFixed(2)}`

/** One path, drawn twice: the scale, and however much of it the reading takes. */
const PATH = `M${at(START)} A ${R} ${R} 0 ${SWEEP > 180 ? 1 : 0} 1 ${at(END)}`

/**
 * The score against its scale, with the figure inside it.
 *
 * It was a bar that filled, which is the shape of a task in progress — at eighty-five it read as
 * nearly finished, when eighty-five is a good score and nobody is working towards a hundred. An
 * arc states a proportion instead of a journey.
 *
 * `ghost` draws the scale with nothing on it and a hundred where the figure goes: the widest
 * reading there is, so whatever lands is never wider than the placeholder and the card does not
 * shift when the report arrives.
 */
export const ScoreArc = ({
	score,
	ghost = false,
}: { score?: number; ghost?: boolean }) => {
	const reading = ghost ? 0 : Math.max(0, Math.min(100, score ?? 0))

	return (
		<View style={{ width: WIDTH, height: HEIGHT }}>
			<Svg width={WIDTH} height={HEIGHT}>
				<Path
					d={PATH}
					fill="none"
					stroke={ghost ? GHOST.track : colors.ground}
					strokeWidth={STROKE}
					strokeLinecap="round"
				/>
				{!ghost && (
					<Path
						d={PATH}
						fill="none"
						stroke={colors.accent}
						strokeWidth={STROKE}
						strokeLinecap="round"
						strokeDasharray={[(LENGTH * reading) / 100, LENGTH]}
					/>
				)}
			</Svg>
			<View
				className="items-center"
				style={{ position: 'absolute', left: 0, right: 0, top: CY - BLOCK / 2 }}
			>
				<Header
					variant="display"
					className="text-[54px] leading-[54px]"
					style={{ color: ghost ? GHOST.type : colors.ink }}
				>
					{ghost ? '100' : String(score ?? 0)}
				</Header>
				<Text
					size="body3"
					className="uppercase tracking-[0.08em]"
					style={{ marginTop: LABEL_GAP, color: ghost ? GHOST.type : colors.muted }}
				>
					of 100
				</Text>
			</View>
		</View>
	)
}

/** The same pair the Care chart draws an empty slot in, so the app has one way of saying this. */
const GHOST = {
	type: 'rgba(125,111,111,0.22)',
	track: 'rgba(125,111,111,0.13)',
}
